<template>
  <div class="w-full">
    <a-dropdown
      v-model:visible="dropdownVisible"
      placement="bottomLeft"
      overlay-class-name="tag-selector-dropdown"
      trigger="click"
    >
      <template #default>
        <a-input
          ref="inputRef"
          class="overflow-hidden"
          v-model:value="inputValue"
          :placeholder="selectedGroup.length ? '' : placeholder"
          @focus="handleFocus"
          @keydown.delete="handleDelete"
        >
          <template #prefix>
            <!-- 单选时不使用tag列表，直接展示所选label -->
            <div v-if="selectedGroup.length" @click="() => (dropdownVisible = true)" class="w-full">
              <div v-if="isMultiple" class="w-full flex items-center">
                <div class="flex items-center w-full flex-wrap">
                  <a-tag
                    v-for="(item, index) in selectedGroup"
                    :key="item"
                    closable
                    @close.stop="removeTag(item)"
                    class="!bg-[#f5f5f5] !border-none !py-[3px] !my-[2px] !pl-[8px] !mr-[2px]"
                    v-show="index < maxTagCount"
                  >
                    {{ getLabel(item) }}
                  </a-tag>
                  <a-tooltip
                    class="!my-[2px] !ml-[5px]"
                    v-show="selectedGroup.length > maxTagCount"
                  >
                    <template #title>
                      <div
                        v-for="item in selectedGroup.slice(maxTagCount, selectedGroup.length)"
                        :key="item"
                      >
                        {{ findName(item) }}
                      </div>
                    </template>
                    <span
                      v-show="selectedGroup.length > maxTagCount"
                      class="inline-block bg-[#f5f5f5] px-[8px] py-[2px] rounded-[4px]"
                    >
                      + {{ selectedGroup.length - maxTagCount }} ...
                    </span>
                  </a-tooltip>
                </div>
              </div>
              <span v-else>
                {{ getLabel(selectedGroup[0]) }}
              </span>
            </div>
          </template>
        </a-input>
      </template>

      <template #overlay>
        <div
          class="w-full max-h-[300px] overflow-y-auto bg-white shadow-[0_4px_10px_rgba(0,0,0,0.12)] rounded-lg"
        >
          <template v-if="filterOptions.length">
            <div
              v-for="(group, index) in filterOptions"
              :key="group.value"
              class="border-[#f5f5f5]"
              :class="{ ' border-b': index !== filterOptions.length - 1 }"
            >
              <!-- 父级 -->
              <div
                class="flex items-center justify-between py-[8px] cursor-pointer font-medium hover:text-[#165dfe] px-3"
                :class="{ '!bg-[#E3EEFE]': isChecked(group) }"
                @click.prevent.stop.passive="handleClick(group)"
              >
                <span>{{ group.label }}</span>
                <Fragment>
                  <CheckOutlined v-if="isChecked(group)" class="mr-3 !text-[#165dfe]" />
                  <DownOutlined
                    v-if="group?.children?.length"
                    class="text-xs transition-transform duration-200"
                    :class="{ '-rotate-180': expanded[group.value] }"
                    @click.stop="toggleExpand(group)"
                  />
                </Fragment>
              </div>
              <!-- 子级 -->
              <transition
                v-if="group?.children?.length"
                enter-active-class="transition-all duration-200 ease-in"
                leave-active-class="transition-all duration-200 ease-in"
                enter-from-class="opacity-0 -translate-y-1"
                leave-to-class="opacity-0 -translate-y-1"
              >
                <div v-if="expanded[group.value]" class="py-1 pl-4 bg-[#F6F6F6]">
                  <div
                    class="h-[32px] leading-[32px]"
                    v-for="child in group.children ?? []"
                    :key="child.value"
                  >
                    <span>{{ child.label }}</span>
                  </div>
                </div>
              </transition>
            </div>
          </template>
          <template v-else>
            <Empty :image="Empty.PRESENTED_IMAGE_SIMPLE" description="无搜索结果" />
          </template>
        </div>
      </template>
    </a-dropdown>
  </div>
</template>

<script lang="ts" setup>
  import { ref, computed, watchEffect, nextTick } from 'vue'
  import { Empty } from 'ant-design-vue'
  import { CheckOutlined, DownOutlined } from '@ant-design/icons-vue'
  import { basicProps } from './props'

  const props = defineProps(basicProps)
  const isMultiple = props.mode == 'multiple'
  const emit = defineEmits<{
    (e: 'change', value: string[]): void
    (e: 'update:value', value: string[]): void
  }>()

  const inputValue = ref<string>('')
  const dropdownVisible = ref<boolean>(false)
  const expanded = ref<Record<string, boolean>>({})
  const inputRef = ref<InstanceType<typeof HTMLInputElement> | null>(null)

  const normalizedValue = computed(() => {
    // 确定最终的值（优先取value，其次defaultValue）
    let val =
      props.value !== undefined && props.value !== null && props.value !== ''
        ? props.value
        : props.defaultValue

    if (isMultiple) {
      // 多选：如果是字符串就转为数组，否则如果是数组就直接用
      if (typeof val === 'string') {
        return val ? [val] : []
      }
      if (Array.isArray(val)) {
        return val
      }
      return []
    } else {
      // 单选：直接返回原始值
      return val
    }
  })

  const selectedGroup = ref<any>([...normalizedValue.value])

  watchEffect(() => {
    selectedGroup.value = [...normalizedValue.value]
  })

  const placeholder = computed(() => props.placeholder)

  /**
   * 获取选项的label
   */
  const getLabel = (value: string): string => {
    const option = props.options.find((item: any) => item.value === value)
    return option?.label ?? value
  }

  /**
   * 筛选后的选项
   */
  const filterOptions = computed(() => {
    return props.options.filter((item: any) =>
      item.label.toLowerCase().includes(inputValue.value.toLowerCase()),
    )
  })

  /**
   * 根据expandedKeys来默认展开某选项
   */
  const updateExpanded = () => {
    expanded.value = (props.expandedKeys || []).reduce<Record<string, boolean>>((acc, cur) => {
      acc[cur as string] = true
      return acc
    }, {})
  }
  watchEffect(updateExpanded)

  /**
   * 选择项是否选中
   */
  const isChecked = (group: any): boolean => selectedGroup.value.includes(group.value)

  /**
   * 选择项展开事件
   */
  const toggleExpand = (group: any) => {
    expanded.value[group.value] = !expanded.value[group.value]
  }

  /**
   * 选择项点击事件
   */
  const handleClick = (group: any) => {
    if (isMultiple) {
      const idx = selectedGroup.value.indexOf(group.value)
      if (idx === -1) {
        selectedGroup.value.push(group.value)
      } else {
        selectedGroup.value.splice(idx, 1)
      }
    } else {
      // 单选模式，点击即选中唯一项
      if (selectedGroup.value[0] !== group.value) {
        selectedGroup.value = [group.value]
      }
      dropdownVisible.value = false
    }
    nextTick(() => {
      isMultiple && inputRef.value?.focus?.()
      inputValue.value = ''
    })
    emit('change', [...selectedGroup.value])
    emit('update:value', [...selectedGroup.value])
  }

  /**
   * 输入框聚焦事件
   */
  const handleFocus = () => {
    inputValue.value = ''
  }

  /**
   * 输入框删除事件（删除最后一个标签/单选时清空选择）
   */
  const handleDelete = () => {
    if (!inputValue.value.length && selectedGroup.value.length > 0) {
      if (isMultiple) {
        selectedGroup.value.splice(-1, 1)
      } else {
        selectedGroup.value = []
      }
      emit('change', [...selectedGroup.value])
      emit('update:value', [...selectedGroup.value])
    }
  }

  /**
   * 选择项删除事件
   */
  const removeTag = (item: string) => {
    if (isMultiple) {
      selectedGroup.value = selectedGroup.value.filter((v) => v !== item)
    } else {
      selectedGroup.value = []
    }
    emit('change', [...selectedGroup.value])
    emit('update:value', [...selectedGroup.value])
  }

  /**
   * 找到对应label
   */
  const findName = (value: string) => {
    const option = props.options.find((item: any) => item.value === value)
    return option?.label ?? value
  }
</script>

<style lang="less" scoped>
  ::v-deep #form_item_source_tags {
    min-width: 20px !important;
  }
  ::v-deep .ant-input-affix-wrapper {
    padding: 1px 4px !important;
  }
</style>
